import { useEffect, useRef, useState } from 'react';
import { formatMoney, parseMoney, PAYMENT_METHODS, type PaymentMethod } from '@makila/fiscal';
import { CASH_MOVEMENT_LABEL, cashReportX, closeCashSession, countTotal, MZN_DENOMINATIONS, recordCashMovement, type CashMovementKind, type CashReport, type TerminalIdentity } from '@makila/pos-core';
import { Printer, X } from 'lucide-react';
import type { DeviceState } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import { cashReportHtml } from '../lib/receipt';
import { openDrawer, printThermal } from '../lib/hw/printer';
import type { Operator } from '../screens/Lock';

type View = 'menu' | 'move' | 'close';

/** Caixa: relatório X, sangria/reforço, abrir gaveta (com motivo) e fecho Z por contagem. */
export function CashPanel(p: {
  store: IdbTerminalStore; device: DeviceState; who: TerminalIdentity; operator: Operator; operatorName: (id: string) => string;
  onClose: () => void; onChanged: () => void; onClosedSession: (z: CashReport) => void;
}) {
  const dlg = useRef<HTMLDialogElement>(null);
  const [view, setView] = useState<View>('menu');
  const [x, setX] = useState<CashReport | null>(null);
  const [kind, setKind] = useState<CashMovementKind>('cash_out');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [counted, setCounted] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Gorjeta: a quem (empregado) e como foi dada.
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [beneficiary, setBeneficiary] = useState('');
  const [tipMethod, setTipMethod] = useState<PaymentMethod>('NU');
  useEffect(() => { void p.store.catalog('operator').then((i) => setStaff(i.map((o) => ({ id: o.id, name: String(o.data.name), active: !!o.data.active })).filter((o) => o.active))); }, [p.store]);

  useEffect(() => { dlg.current?.showModal(); void cashReportX(p.store).then(setX); }, [p.store]);

  const print = (r: CashReport) => void printThermal(cashReportHtml(r, p.device, (id) => (id ? p.operatorName(id) : '')));
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  let countedTotal = '0.00';
  try { countedTotal = countTotal(counted); } catch { /* quantidades inválidas: o botão fica desligado */ }

  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); if (!busy) p.onClose(); }} className="m-auto w-[min(680px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">{view === 'close' ? 'Fechar a caixa (Z)' : view === 'move' ? CASH_MOVEMENT_LABEL[kind] : 'Caixa'}</h2>
        <button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="max-h-[75vh] overflow-y-auto p-5">
        {view === 'menu' && x && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Vendas" value={formatMoney(x.grossTotal)} />
              <Stat label="Documentos" value={String(x.documents.count)} />
              <Stat label="Numerário na gaveta" value={formatMoney(x.expectedCash)} />
              <Stat label="Fundo" value={formatMoney(x.openingFloat)} />
            </div>
            <ul className="mt-4 space-y-1 text-sm">
              {x.byMethod.map((m) => <li key={m.method} className="flex justify-between rounded-lg bg-surface-2 px-3 py-2"><span>{m.label}</span><span className="num font-semibold">{formatMoney(m.amount)}</span></li>)}
            </ul>
            {x.tips.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm">
                {x.tips.map((t) => <li key={t.beneficiaryId} className="flex justify-between rounded-lg bg-surface-2 px-3 py-2"><span>Gorjetas — {p.operatorName(t.beneficiaryId)}</span><span className="num font-semibold">{formatMoney(t.cash)}</span></li>)}
              </ul>
            )}
            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-5">
              <Big onClick={() => print(x)}><Printer size={18} /> Relatório X</Big>
              <Big onClick={() => { setKind('cash_out'); setView('move'); }}>Sangria</Big>
              <Big onClick={() => { setKind('cash_in'); setView('move'); }}>Reforço</Big>
              <Big onClick={() => { setKind('drawer_open'); setView('move'); }}>Abrir gaveta</Big>
              <Big onClick={() => { setKind('tip'); setBeneficiary(p.operator.id); setView('move'); }}>Gorjeta</Big>
            </div>
            <button onClick={() => setView('close')} className="mt-3 h-14 w-full rounded-xl bg-brand-700 text-lg font-bold text-white">Fechar a caixa (relatório Z)</button>
          </>
        )}

        {view === 'move' && (
          <form onSubmit={(e) => { e.preventDefault(); void run(async () => {
            const a = kind === 'drawer_open' ? '0' : parseMoney(amount);
            if (a === null) throw new Error('Valor inválido');
            await recordCashMovement(p.store, p.who, { kind, amount: a, reason, operatorId: p.operator.id, ...(kind === 'tip' ? { beneficiaryId: beneficiary, method: tipMethod } : {}) });
            // Gaveta: com impressora directa abre-se já (o movimento ficou registado antes).
            if (kind !== 'tip' || tipMethod === 'NU') void openDrawer();
            p.onChanged();
            p.onClose();
          }); }}>
            <p className="text-muted">
              {kind === 'cash_out' && 'Dinheiro retirado da gaveta (ex.: depósito no banco, pagamento a fornecedor).'}
              {kind === 'cash_in' && 'Dinheiro posto na gaveta (ex.: moedas para trocos).'}
              {kind === 'drawer_open' && 'Abrir a gaveta sem venda fica registado com o seu nome e o motivo.'}
              {kind === 'tip' && 'Gorjeta do cliente: fica fora do talão e da factura. No fecho, o Z diz quanto entregar a cada empregado.'}
            </p>
            {kind === 'tip' && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <select aria-label="Empregado" value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} className="h-12 rounded-xl border border-base bg-surface-2 px-3">
                  {staff.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <select aria-label="Meio da gorjeta" value={tipMethod} onChange={(e) => setTipMethod(e.target.value as PaymentMethod)} className="h-12 rounded-xl border border-base bg-surface-2 px-3">
                  {(['NU', 'MP', 'EM', 'CC'] as const).map((m) => <option key={m} value={m}>{PAYMENT_METHODS[m]}</option>)}
                </select>
              </div>
            )}
            {kind !== 'drawer_open' && (
              <input autoFocus inputMode="decimal" aria-label="Valor" placeholder="0,00" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="num mt-3 h-14 w-full rounded-xl border border-base bg-surface-2 px-4 text-right text-2xl" />
            )}
            {kind !== 'tip' && <input autoFocus={kind === 'drawer_open'} required aria-label="Motivo" placeholder="Motivo (obrigatório)" value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-3 h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setView('menu')} className="h-14 rounded-xl border border-base font-medium">Voltar</button>
              <button disabled={busy} className="h-14 rounded-xl bg-brand-700 font-bold text-white disabled:opacity-40">Registar</button>
            </div>
          </form>
        )}

        {view === 'close' && x && (
          <>
            <p className="text-muted">Conte as notas e moedas da gaveta. O sistema só mostra o esperado depois da contagem, para não a influenciar.</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {MZN_DENOMINATIONS.map((d) => (
                <label key={d} className="flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2">
                  <span className="num w-16 font-semibold">{formatMoney(d, Number(d) < 1 ? 2 : 0)}</span>
                  <span className="text-muted">×</span>
                  <input inputMode="numeric" aria-label={`Quantidade de ${d} MT`} value={counted[d] ?? ''} placeholder="0"
                    onChange={(e) => { const n = e.target.value.replace(/\D/g, ''); setCounted((c) => ({ ...c, [d]: n === '' ? 0 : Number(n) })); }}
                    className="num h-12 w-full min-w-0 rounded-lg border border-base bg-[var(--surface)] px-2 text-right text-lg" />
                </label>
              ))}
            </div>
            <div className="num mt-4 flex items-baseline justify-between text-2xl font-bold"><span>Contado</span><span>{formatMoney(countedTotal)} MT</span></div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setView('menu')} className="h-14 rounded-xl border border-base font-medium">Voltar</button>
              <button disabled={busy} onClick={() => void run(async () => {
                const z = await closeCashSession(p.store, p.who, { operatorId: p.operator.id, counted: Object.fromEntries(Object.entries(counted).filter(([, n]) => n > 0)) });
                print(z);
                p.onClosedSession(z);
              })} className="h-14 rounded-xl bg-brand-700 font-bold text-white disabled:opacity-40">Fechar e imprimir Z</button>
            </div>
          </>
        )}
        {error && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
      </div>
    </dialog>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl bg-surface-2 p-3"><div className="text-xs text-muted">{label}</div><div className="num text-lg font-bold">{value}</div></div>;
}

function Big({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return <button onClick={onClick} className="flex h-14 items-center justify-center gap-2 rounded-xl border border-base font-semibold hover:bg-surface-2">{children}</button>;
}
